import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import {
  type AmountLimitViolation,
  boundsForPayer,
  type CorridorLimits,
  resolveAmountLimitViolation,
} from "@/shared/utils/amountLimits";

import type { RuntimeCorridor } from "../constants/dispersions.constants";
import type { PayoutRail } from "../services/paymentsPayout.service";

export interface PayoutLimitViolation {
  kind: AmountLimitViolation;
  /** The bound that was crossed, as a decimal string in the corridor currency. */
  bound: string;
}

const field = (fields: Record<string, unknown> | null | undefined, key: string): string =>
  String(fields?.[key] ?? "").trim();

/**
 * Mirrors the backend's inference from the destination: a NIT (Transfiya), a CNPJ key (PIX) or a 12-character
 * RFC (SPEI) identify a company; everything else — a Bre-B key, a CBU/CVU, an unknown field — is checked as a
 * person, the stricter kind.
 */
export function payoutPayerTypeFor(
  corridor: RuntimeCorridor,
  rail: PayoutRail,
  recipientFields: Record<string, unknown> | null | undefined,
): LimitPayerType {
  switch (corridor) {
    case "COL":
      return rail === "TRANSFIYA" && field(recipientFields, "transfiya_document_type").toUpperCase() === "NIT"
        ? "COMPANY"
        : "PERSON";
    case "BRA":
      return field(recipientFields, "pix_type").toUpperCase() === "CNPJ" ? "COMPANY" : "PERSON";
    // Measured raw, exactly as the runtime does — stripping separators here would call a hyphenated RFC a
    // company while the backend still read it as an individual.
    case "MEX":
      return field(recipientFields, "rfc").length === 12 ? "COMPANY" : "PERSON";
    default:
      return "PERSON";
  }
}

/**
 * The runtime compares what the beneficiary receives (the quote's `payout_net`, what a payout persists as
 * `net_amount`), never the debit. `payerType: null` — the beneficiary is not known yet — checks the widest
 * envelope of both kinds, exactly as the backend does on the quote.
 */
export function resolvePayoutLimitViolation(input: {
  limits: CorridorLimits | null;
  payerType: LimitPayerType | null;
  deliveredAmount: string | null | undefined;
}): PayoutLimitViolation | null {
  const bounds = boundsForPayer(input.limits, input.payerType);
  const kind = resolveAmountLimitViolation(input.deliveredAmount, bounds);
  if (!kind || !bounds) return null;
  return { kind, bound: (kind === "above_maximum" ? bounds.max_amount : bounds.min_amount) ?? "0" };
}
